/**
 * A login form component used for district flows that link accounts
 *
 * Currently used for districts activation and Clever Library
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {GoogleButton} from "@khan/auth-google";
import {getLoginErrorMessageByCode} from "@khan/auth-password";
import {redirect} from "@khan/urls";

import IdentityTextField from "#auth/components/identity-text-field.tsx";
import PasswordField from "#auth/components/password-field.tsx";

type Props = {
    isCleverLibrary: boolean;
    isTeacher: boolean;
    cleverToken?: string;
    disableEmail?: boolean;
    defaultIdentifier?: string;
    continueUrl: string;
    onForgotPasswordClick: () => void;
    onLoginError?: () => void;
    handleLogin: (identifier: string, password: string) => Promise<any>;
    formTitle?: string;
    formMessage?: string;
    /**
     * Optional identifier to use for the dialog title.
     *
     * Usefult for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
};

type State = {
    identifier: string;
    password: string;
    submitting: boolean;
    error?: string;
    ssoIsRedirecting: boolean;
};

class LinkAccountLoginForm extends React.Component<Props, State> {
    state: State = {
        identifier: this.props.defaultIdentifier || "",
        password: "",
        submitting: false,
        ssoIsRedirecting: false,
        error: "",
    };

    handleGoogleLoginError: (message: string) => void = (message: string) => {
        this.setState({error: message});
    };

    submitLogin: (e: React.SyntheticEvent) => void = (
        e: React.SyntheticEvent,
    ) => {
        const {identifier, password} = this.state;
        const {handleLogin, onLoginError} = this.props;

        e.preventDefault();

        this.setState({submitting: true});

        handleLogin(identifier, password).catch((res) => {
            const errorMessage = getLoginErrorMessageByCode(res.message);

            onLoginError && onLoginError();

            this.setState({
                submitting: false,
                error: errorMessage,
            });
        });
    };

    render(): React.ReactNode {
        const {
            isCleverLibrary,
            isTeacher,
            onForgotPasswordClick,
            continueUrl,
            formTitle,
            formMessage,
            disableEmail,
            defaultIdentifier,
            cleverToken,
            titleId,
        } = this.props;
        const {identifier, password, ssoIsRedirecting, error, submitting} =
            this.state;

        return (
            <>
                <HeadingMedium id={titleId}>
                    {formTitle ? formTitle : t`Log in to your existing account`}
                </HeadingMedium>
                {formMessage ? (
                    <View>
                        <Strut size={spacing.small_12} />
                        <Body style={styles.formMessage}>{formMessage}</Body>
                        <Strut size={spacing.small_12} />
                    </View>
                ) : (
                    <Strut size={spacing.large_24} />
                )}
                <GoogleButton
                    role={isTeacher ? "teacher" : "learner"}
                    linkClever={isCleverLibrary}
                    cleverTransferToken={cleverToken}
                    disabled={submitting}
                    continueUrl={continueUrl}
                    onError={this.handleGoogleLoginError}
                    onSuccess={(redirectTo) => {
                        redirect(redirectTo);
                        this.setState({ssoIsRedirecting: true});
                    }}
                    forceSelectAccount={true}
                />
                {error ? (
                    <View>
                        <Strut size={spacing.small_12} />
                        <Body style={styles.error}>{this.state.error}</Body>
                    </View>
                ) : (
                    <Strut size={spacing.medium_16} />
                )}
                <form onSubmit={this.submitLogin}>
                    <IdentityTextField
                        testId="identifier"
                        label={
                            isCleverLibrary
                                ? t`School email`
                                : t`Email or username`
                        }
                        emptyMessage={t`An email or username is required to log in. You can also log in with Google.`}
                        placeholder={
                            isCleverLibrary
                                ? t`example@school.edu`
                                : t`email@example.com`
                        }
                        disabled={disableEmail && !!defaultIdentifier}
                        value={identifier || null}
                        onValidation={(isValid, value) =>
                            this.setState({identifier: isValid ? value : ""})
                        }
                        onFocus={() => {
                            this.setState({error: ""});
                        }}
                    />
                    <PasswordField
                        testId="password"
                        emptyMessage={t`A password is required to log in.`}
                        label={t`Password`}
                        value={password || null}
                        onValidation={(isValid, value) =>
                            this.setState({password: isValid ? value : ""})
                        }
                        onFocus={() => {
                            this.setState({error: ""});
                        }}
                    />

                    <View style={styles.row}>
                        <Button
                            kind="tertiary"
                            size="small"
                            disabled={submitting}
                            onClick={onForgotPasswordClick}
                        >
                            {t`Forgot password?`}
                        </Button>
                        <Spring />
                    </View>
                    <Button
                        type="submit"
                        disabled={submitting || !identifier || !password}
                        spinner={submitting || ssoIsRedirecting}
                    >
                        {t`Log in`}
                    </Button>
                </form>
            </>
        );
    }
}

const styles = StyleSheet.create({
    error: {
        color: color.red,
        fontSize: 12,
    },
    formMessage: {
        color: color.offBlack50,
        fontSize: 14,
    },
    row: {
        flexDirection: "row",
    },
});

export default LinkAccountLoginForm;
